/**
 * Codemade OÜ
 *
 * RewardDeals.co.uk website frontend app entry point
 *
 * @summary RewardDeals.co.uk website frontend app entry point
 * @author Borka Martin Orlov
 *
 * Created at     : 2020-08-11 21:23:46
 * Last modified  : 2021-04-07 18:40:30
 */

import './bootstrap';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import Vuex from 'vuex';
import router from './routes';
import store from './store/index';

window.Vue = require('vue');


// Initialize sentry for error tracking.
// Sentry.io
const version = `rewarddeals-vue@${process.env.MIX_APP_VERSION}`;
Sentry.init({
    Vue,
    dsn: "https://8721bbb7ddc84165bc0bd92c99c54c16@o191030.ingest.sentry.io/4504095643926528",
    integrations: [
        new BrowserTracing({
            routingInstrumentation: Sentry.vueRouterInstrumentation(router),
            tracingOrigins: ["localhost", "rewarddeals.co.uk", /^\//],
        }),
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.5,
});


const app = new Vue({
    el: '#rewarddeals-wrapper',
    router,
    store: new Vuex.Store(store),

    metaInfo: {
        title: 'Share a great deal, earn a great deal',
        // all titles will be injected into this template
        titleTemplate: '%s | RewardDeals',
    },

    created() {
        /**
         * If the user is already stored in localstorage,
         * then load the data from localstorage to state
         * */
        if (this.getCookie('access_token') && this.getCookie('account_data')) {
            const user = {
                access_token: this.getCookie('access_token'),
                user: JSON.parse(this.getCookie('account_data')),
            };
        
            this.$store.commit('login', user);
        }

        /* *
         * HTTP 401 response listener
         * Should any response return 401 (unauthorized):
         * redirect to home and 'logout' local data
         * */
        window.axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response.status === 401) {
                    Vue.toasted.show(
                        'Please login to perform this action.', 
                        { duration: 6000 },
                    );

                    this.$store.commit('logout');
                }  
                
                if (error.response.status === 403) {
                    Vue.toasted.show(
                        'In order to complete your registration you must verify your email address, by clicking the link we have emailed to you. You will then have full access and be able to like, share and report expired deals to start earning gift card stars. Contact support@rewarddeals.co.uk if you need assistance. ', 
                        { duration: 6000 },
                    );
                } 
                
                if (error.response.status >= 400) {
                    console.log(error.response.data.error);
                    Vue.toasted.show(error.response.data.error, { duration: 6000 });
                }

                return Promise.reject(error);
            },
        );
    },

    methods: {
        // Helper functions to set, get, and delete cookies
        setCookie(name, value, days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            const expires = '; expires=' + date.toUTCString();
            document.cookie = name + "=" + (value || "") + expires + "; path=/";
        },

        getCookie(name) {
            const nameEQ = name + "=";
            const ca = document.cookie.split(';');
            for (let i = 0; i < ca.length; i++) {
                let c = ca[i];
                while (c.charAt(0) === ' ') c = c.substring(1, c.length);
                if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
            }
            return null;
        },  

        deleteCookie(name) {   
            this.setCookie(name, '', -1);
        },
    },
});

export default app;
